import React from 'react'
import strings from 'utils/strings'
import { useDispatch } from 'react-redux'
import { RESET, SAVE, SAVE_LAYOUT, DASH_VIEW } from 'utils/constants'
import { useSelector } from 'hooks/redux'
import { setDrawerVisibleWidth, setDrawerWidth } from 'actions/drawer'
import { updateUserSetting } from 'actions/user'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import {
  DEFAULT_DRAWER_WIDTH,
  SWGA_BUILDER_INVENTORY_WIDTH,
} from 'utils/drawerConstants'
import USER_SETTINGS from 'constants/userSettings'
import {
  getDashHasChanges,
  getDashVehicleSerialNumber,
  getIsLeadPreProduction,
  hasStagedComponents,
  getHasApprovedLayout,
  getIsSaving,
  getIsDragging,
} from 'selectors/dash'
import {
  getCanApproveDashLayout,
  getCanEditSwitchAndGauge,
} from 'selectors/authorization'
import { Button, FlexRow, FlexCol } from 'components/patterns'
import DraggableList from 'components/SwitchAndGauge/DashDrawer/DraggableList'
import { Content, Footer } from 'components/SwitchAndGauge/DashDrawer/Sections'
import Breadcrumb from 'components/SwitchAndGauge/DashDrawer/Breadcrumb'
import { getDrawerWidth } from 'selectors/drawer'
import DisplayAllCheckbox from './DisplayAllCheckbox'
import PartTableView from './PartTableView'

import {
  StyledViewWrap,
  StyledChevronCaret,
  StyledButton,
  StyledTableHeader,
  StyledStagingSaveErrorMessage,
} from './style'

const ListsView = () => {
  const dispatch = useDispatch()
  const dashHasChanges = useSelector(getDashHasChanges)
  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const canEdit = useSelector(getCanEditSwitchAndGauge, { serialNumber })
  const hasComponentStaged = useSelector(hasStagedComponents)
  const drawerWidth = useSelector(getDrawerWidth)
  const isLeadPreProduction = useSelector(getIsLeadPreProduction)
  const canApproveLayout = useSelector(getCanApproveDashLayout)
  const hasApprovedLayout = useSelector(getHasApprovedLayout)
  const isSaving = useSelector(getIsSaving)
  const isDragging = useSelector(getIsDragging)

  const handleReset = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: RESET,
      })
    )
  }

  const defaultDrawerWidth = drawerWidth === DEFAULT_DRAWER_WIDTH

  const handleToggleDrawerWidth = () => {
    const width = defaultDrawerWidth
      ? SWGA_BUILDER_INVENTORY_WIDTH
      : DEFAULT_DRAWER_WIDTH
    dispatch(setDrawerWidth(width))
    dispatch(setDrawerVisibleWidth(width))

    dispatch(
      updateUserSetting(
        USER_SETTINGS.BUILDER_DRAWER_EXTENDED_WIDTH,
        width === SWGA_BUILDER_INVENTORY_WIDTH
      )
    )
  }

  const handleSave = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: SAVE,
      })
    )
  }

  const handleApproveLayoutOnClick = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: SAVE_LAYOUT,
      })
    )
  }

  const saveComponentChangesFooter =
    defaultDrawerWidth && canEdit && !isSaving ? (
      <Footer>
        <FlexRow>
          <FlexCol span={12}>
            <div>
              <Button
                type="secondary"
                disabled={!dashHasChanges}
                onClick={handleReset}
              >
                {strings.reset}
              </Button>
              {hasComponentStaged && (
                <StyledStagingSaveErrorMessage>
                  {strings.saving_dash_error}
                </StyledStagingSaveErrorMessage>
              )}
            </div>
          </FlexCol>
          <FlexCol span={12} alignItems="end">
            <div>
              <Button
                disabled={!dashHasChanges || hasComponentStaged}
                onClick={handleSave}
              >
                {strings.save}
              </Button>
            </div>
          </FlexCol>
        </FlexRow>
      </Footer>
    ) : null

  const approveLayoutFooter =
    isLeadPreProduction && canApproveLayout ? (
      <Footer>
        <FlexRow>
          <FlexCol span={24} alignItems="end">
            <div>
              <Button
                disabled={hasApprovedLayout}
                onClick={handleApproveLayoutOnClick}
              >
                {strings.save_layout}
              </Button>
            </div>
          </FlexCol>
        </FlexRow>
      </Footer>
    ) : null

  const renderContent = defaultDrawerWidth ? (
    <DraggableList />
  ) : (
    <PartTableView onToggleListView={handleToggleDrawerWidth} />
  )

  const showApproveLayoutFooter =
    getIsLeadPreProduction && canApproveLayout && !dashHasChanges
  const footer = showApproveLayoutFooter
    ? approveLayoutFooter
    : saveComponentChangesFooter

  return (
    <StyledViewWrap>
      <StyledTableHeader>
        <Breadcrumb />
        <StyledButton link onClick={() => handleToggleDrawerWidth()}>
          <StyledChevronCaret
            icon="icons-expand-collapse-double-chevron"
            defaultDrawerWidth={defaultDrawerWidth}
            fill="drawerWideCaretColor"
          />
          {defaultDrawerWidth ? strings.table_view : strings.back_to_list_view}
        </StyledButton>
      </StyledTableHeader>
      {defaultDrawerWidth && <DisplayAllCheckbox />}
      <Content data-dragging={isDragging || undefined}>{renderContent}</Content>
      {footer}
    </StyledViewWrap>
  )
}

export default ListsView
